import React from 'react';

interface Realtor {
  name: string;
  image?: string;
}

interface Affiliate {
  level: 1 | 2;
  name: string;
  image?: string;
  properties: number;
}

interface AffiliateNetworkProps {
  realtor: Realtor;
  affiliates: Affiliate[];
  className?: string;
}

const AffiliateNetwork: React.FC<AffiliateNetworkProps> = ({
  realtor,
  affiliates,
  className = ''
}) => {
  const level1Affiliates = affiliates.filter(a => a.level === 1);
  const level2Affiliates = affiliates.filter(a => a.level === 2);

  const AvatarComponent: React.FC<{ name: string; image?: string; size?: 'sm' | 'md' | 'lg' }> = ({ 
    name, 
    image, 
    size = 'md' 
  }) => {
    const sizeClasses = {
      sm: 'w-8 h-8 text-xs',
      md: 'w-10 h-10 text-sm',
      lg: 'w-12 h-12 text-base'
    };

    if (image) {
      return (
        <img
          src={image}
          alt={name}
          className={`${sizeClasses[size]} rounded-full object-cover border-2 border-white shadow-sm`}
        />
      );
    }

    return (
      <div className={`${sizeClasses[size]} rounded-full bg-primary text-white flex items-center justify-center font-medium border-2 border-white shadow-sm`}>
        {name.charAt(0).toUpperCase()}
      </div>
    );
  };

  return (
    <div className={`${className}`}>
      {/* Realtor (Center) */}
      <div className="flex flex-col items-center mb-6">
        <AvatarComponent name={realtor.name} image={realtor.image} size="lg" />
        <p className="text-sm font-medium text-gray-900 mt-2">{realtor.name}</p>
        <p className="text-xs text-gray-500">You</p>
      </div>

      {/* Level 1 Affiliates */}
      {level1Affiliates.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-center mb-3">
            <div className="h-px bg-gray-300 flex-1"></div>
            <span className="px-3 text-xs font-medium text-gray-500 bg-white">Level 1</span>
            <div className="h-px bg-gray-300 flex-1"></div>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3">
            {level1Affiliates.slice(0, 4).map((affiliate, index) => (
              <div key={index} className="flex flex-col items-center">
                <AvatarComponent name={affiliate.name} image={affiliate.image} size="md" />
                <p className="text-xs font-medium text-gray-700 mt-1 text-center max-w-16 truncate">
                  {affiliate.name.split(' ')[0]}
                </p>
                <p className="text-xs text-gray-500">{affiliate.properties} props</p>
              </div>
            ))}
            
            {level1Affiliates.length > 4 && (
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-xs font-medium border-2 border-white shadow-sm">
                  +{level1Affiliates.length - 4}
                </div>
                <p className="text-xs text-gray-500 mt-1">more</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Level 2 Affiliates */}
      {level2Affiliates.length > 0 && (
        <div>
          <div className="flex items-center justify-center mb-3">
            <div className="h-px bg-gray-300 flex-1"></div>
            <span className="px-3 text-xs font-medium text-gray-500 bg-white">Level 2</span>
            <div className="h-px bg-gray-300 flex-1"></div>
          </div>
          
          <div className="flex flex-wrap justify-center gap-2">
            {level2Affiliates.slice(0, 6).map((affiliate, index) => (
              <div key={index} className="flex flex-col items-center">
                <AvatarComponent name={affiliate.name} image={affiliate.image} size="sm" />
                <p className="text-xs text-gray-600 mt-1 text-center max-w-12 truncate">
                  {affiliate.name.split(' ')[0]}
                </p>
              </div>
            ))}
            
            {level2Affiliates.length > 6 && (
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-xs font-medium border-2 border-white shadow-sm">
                  +{level2Affiliates.length - 6}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Empty State */}
      {affiliates.length === 0 && (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <p className="text-sm text-gray-500">No affiliates yet</p>
          <p className="text-xs text-gray-400 mt-1">Start building your network</p>
        </div>
      )}
    </div>
  );
};

export default AffiliateNetwork;